/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { MouseEvent } from 'react';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { breakpoints } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import Switch from 'bundles/cds-labs/components/Switch/Switch';
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';
import HeadsetSvg from 'bundles/video-player/assets/svg/Headset';
import HighQualitySvg from 'bundles/video-player/assets/svg/HighQualitySvg';
import PlayCircleSvg from 'bundles/video-player/assets/svg/PlayCircleSvg';
import PlaybackSpeedSvg from 'bundles/video-player/assets/svg/PlaybackSpeedSvg';
import SubtitlesSvg from 'bundles/video-player/assets/svg/SubtitlesSvg';
import ModernSettingsMenuItemList from 'bundles/video-player/components/modernSettingsMenu/ModernSettingsMenuItemList';
import ModernSettingsSubmenu from 'bundles/video-player/components/modernSettingsMenu/ModernSettingsSubmenu';
import type { MenuItemList } from 'bundles/video-player/components/modernSettingsMenu/types';
import {
  MODERN_VIDEO_PLAYER_CONTROL_BAR_HEIGHT,
  VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET,
  VIDEO_PLAYER_SUBTITLE_NOT_SET,
} from 'bundles/video-player/constants';
import type { Track, Tracks } from 'bundles/video-player/types/Track';
import type {
  Mp4ResolutionOption,
  PlaybackRate,
  PlaybackRateLabel,
  ResolutionOption,
  VideoPlayerEventingV3UIFunction,
} from 'bundles/video-player/types/VideoPlayer';
import { getResolutionOptions } from 'bundles/video-player/utils/resolutionUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { getEventingDubbingLanguage } from 'bundles/video-player/utils/videoUtils';

import _t from 'i18n!nls/video-player';

const SETTINGS_MENU_OPACITY_TRANSITION_SPEED = 0.2;

const TRACK_COLOR = '#f1f1f1';
const THUMB_COLOR = '#888';
const THUMB_HOVER_COLOR = '#555';

const styles = {
  scrollbar: css`
    /* Custom scrollbar styles for Webkit browsers.
       Some browsers have a tendency to hide scrollbar on page load. Overflow scrolll respects this.
       In order to make sure the scrollbar is always visible, we are going this way
    */
    ::-webkit-scrollbar {
      width: 16px;
    }

    ::-webkit-scrollbar-track {
      background: ${TRACK_COLOR};
    }

    ::-webkit-scrollbar-thumb {
      background: ${THUMB_COLOR};
      border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: ${THUMB_HOVER_COLOR};
    }

    /* Custom scrollbar styles for Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${THUMB_COLOR} ${TRACK_COLOR};

    ${breakpoints.down('xs')} {
      ::-webkit-scrollbar {
        width: 16px;
      }
    }
  `,
  settingsMenuPopup: css`
    max-height: 80%;
    min-width: 256px;
    max-width: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    position: absolute;
    bottom: ${MODERN_VIDEO_PLAYER_CONTROL_BAR_HEIGHT + 24}px;
    right: var(--cds-spacing-400);
    transform: translate(-3%, 0%);
    transition: visibility 0s ${SETTINGS_MENU_OPACITY_TRANSITION_SPEED}s,
      opacity ${SETTINGS_MENU_OPACITY_TRANSITION_SPEED}s linear;
    opacity: 0;
    color: #fff;
    background-color: rgb(0 0 0 / 66%);
    backdrop-filter: blur(23px);
    border-radius: 8px;
    box-shadow: 0 2px 8px 0 rgb(0 0 0 / 32%);
    visibility: hidden;
    padding-top: var(--cds-spacing-150);
    padding-bottom: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      right: 0;
      max-height: 50%;
    }
  `,
  popupVisible: css`
    opacity: 1;
    visibility: visible;
    transition: opacity 0.2s linear;
  `,
  boldIcon: css`
    /* The CDS chevron is not bold enough, so we need to override the styling */
    fill: var(--cds-color-neutral-background-primary);
    stroke: var(--cds-color-neutral-background-primary);
    stroke-width: 1px;
  `,
};

type MainMenuSettingOption = 'autoplay' | 'quality' | 'playbackSpeed' | 'subtitle' | 'audio';

type ModernSettingsMenuItemType = {
  title: string;
  icon?: JSX.Element;
  action?: () => void;
  actionComponent?: JSX.Element;
  isSelected?: boolean;
  buttonWrapper?: boolean;
};

type PropsFromCaller = {
  visible: boolean;
  currentResolutionOption: string;
  currentResolution: string;
  currentPlaybackRate: PlaybackRate;
  supportedPlaybackRates: PlaybackRate[];
  supportedResolutions: string[];
  onResolutionOptionClick: (resolution: ResolutionOption) => void;
  onPlaybackRateClick: (playbackRate: PlaybackRate) => void;
  onSubtitleLanguageChoiceClick: (track: Track, disableTracking?: boolean, isDubbedSubtitle?: boolean) => void;
  onSubtitleOffClick: (isDubbedAudioSelected?: boolean) => void;
  currentTrack: Track | null;
  tracks: Tracks;
  onToggleAutoplay: (disableTracking?: boolean) => void;
  autoplay: boolean;
  enableHls?: boolean;
  dubbedLanguages?: { label: string; languageKey: string }[];
  onDubbedAudioChoiceClick?: (option: { id: string; languageKey: string; label: string }) => void;
  selectedAudioLanguage?: { id: string; languageKey: string; label: string };
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  closeSettingsMenu: () => void;
  isOriginalAudioSelected?: boolean;
  defaultLanguageKey: string;
  handleResolutionChangeForDubbedAudio?: (resolutionOption: ResolutionOption) => void;
};

const ModernSettingsMenuPopup = React.forwardRef(
  (
    {
      visible,
      supportedResolutions,
      currentResolutionOption,
      currentResolution,
      onResolutionOptionClick,
      currentPlaybackRate,
      supportedPlaybackRates,
      onPlaybackRateClick,
      onSubtitleLanguageChoiceClick,
      onSubtitleOffClick,
      currentTrack,
      tracks,
      onToggleAutoplay,
      autoplay,
      enableHls,
      dubbedLanguages,
      onDubbedAudioChoiceClick,
      selectedAudioLanguage,
      trackVideoPlayerClickEvent,
      closeSettingsMenu,
      isOriginalAudioSelected = true,
      defaultLanguageKey,
      handleResolutionChangeForDubbedAudio,
    }: PropsFromCaller,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [settingsSubmenu, setSettingsSubmenu] = useState<MainMenuSettingOption | undefined>(undefined);
    const { isDubbingAvailable } = useDubbingContext();
    // When the modern settings menu is no longer visible, the useEffect will run to reset the settings menu state
    // after the disappearing animation is complete so it looks seamless to learners
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (isInitialRender) {
        setIsInitialRender(false);
      } else {
        const resetSettingsMenuTimeoutRef: NodeJS.Timeout = setTimeout(() => {
          setSettingsSubmenu(undefined);
        }, SETTINGS_MENU_OPACITY_TRANSITION_SPEED * 1000);

        return () => {
          if (resetSettingsMenuTimeoutRef) clearTimeout(resetSettingsMenuTimeoutRef);
        };
      }
      // Disabling so there isn't a loop where setting isInitialRender triggers the useEffect again on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    let settingsSubmenuConfig: MenuItemList | undefined;

    if (settingsSubmenu === 'quality') {
      // We don't translate the label because labels like 720p, 1080p, etc. are internationally recognized resolution indicators.
      const selectedOption = {
        id: currentResolutionOption,
        label:
          currentResolutionOption === 'auto'
            ? `${currentResolutionOption} (${currentResolution})`
            : currentResolutionOption,
      };
      const sortedResolutionOptions = getResolutionOptions({
        supportedResolutions,
        enableHls,
        currentResolutionOption,
        currentResolution,
      });

      settingsSubmenuConfig = {
        id: 'quality',
        title: _t('Quality'),
        selected: selectedOption,
        options: sortedResolutionOptions,
        action: (resolutionOption) => {
          const selectedPossibleResolutionOption = resolutionOption.id as ResolutionOption;
          if (selectedPossibleResolutionOption && currentResolutionOption && isOriginalAudioSelected) {
            trackVideoPlayerClickEvent('video_settings_menu_quality_option', {
              resolution: selectedPossibleResolutionOption,
              previousResolution: currentResolutionOption,
            });
          }

          if (isOriginalAudioSelected) {
            onResolutionOptionClick(selectedPossibleResolutionOption);
          } else {
            handleResolutionChangeForDubbedAudio?.(selectedPossibleResolutionOption);
          }
          closeSettingsMenu();
        },
      };
    } else if (settingsSubmenu === 'playbackSpeed') {
      // We don't translate the label because labels like 1x, 2x, etc. are internationally recognized playback speed indicators.
      const currentPlaybackRateLabel = `${currentPlaybackRate}x` as PlaybackRateLabel;
      const supportedPlaybackRateLabels = supportedPlaybackRates.map((rate) => `${rate}x`) as PlaybackRateLabel[];
      const selectedOption = { id: currentPlaybackRateLabel, label: currentPlaybackRateLabel };
      const playbackRateOptions = supportedPlaybackRateLabels.map((rate) => ({
        id: rate,
        label: rate,
        role: 'menuitemradio',
      }));
      settingsSubmenuConfig = {
        id: 'playbackSpeed',
        title: _t('Playback speed'),
        selected: selectedOption,
        options: playbackRateOptions,
        action: (playbackRateOption) => {
          const playbackRateLabel = playbackRateOption.label;
          const selectedPlaybackRate = parseFloat(playbackRateLabel) as PlaybackRate;
          if (currentPlaybackRate && selectedPlaybackRate) {
            trackVideoPlayerClickEvent('video_settings_menu_playback_speed_option', {
              playbackRate: selectedPlaybackRate,
              previousPlaybackRate: currentPlaybackRate,
            });
          }
          onPlaybackRateClick(selectedPlaybackRate);
          closeSettingsMenu();
        },
      };
    } else if (settingsSubmenu === 'subtitle') {
      // Since subtitles deal with track objects, we'll use the ids as options. Additionally, the track labels are already translated
      // and appear in their native language, which will give users a beneficial experience when selecting a subtitle language.
      const subtitleOffOption = { id: 'off', label: _t('Subtitles off'), role: 'menuitemradio' };
      const selectedOption = currentTrack
        ? { id: currentTrack.language, label: currentTrack.label }
        : subtitleOffOption;
      const trackOptions = tracks.map((track) => ({
        id: track.language,
        label: track.label,
        role: 'menuitemradio',
      }));

      settingsSubmenuConfig = {
        id: 'subtitle',
        title: _t('Subtitles'),
        selected: selectedOption,
        options: [subtitleOffOption, ...trackOptions],
        action: (newTrackOption) => {
          const selectedTrack = tracks.find((track) => track.language === newTrackOption.id);
          trackVideoPlayerClickEvent('video_settings_menu_subtitle_language_option', {
            subtitleLanguage: selectedTrack?.language || VIDEO_PLAYER_SUBTITLE_NOT_SET,
            previousSubtitleLanguage: currentTrack?.language || VIDEO_PLAYER_SUBTITLE_NOT_SET,
          });
          if (!selectedTrack) {
            onSubtitleOffClick(!isOriginalAudioSelected);
          } else {
            onSubtitleLanguageChoiceClick(selectedTrack, true, !isOriginalAudioSelected);
          }
          closeSettingsMenu();
        },
      } as MenuItemList;
    } else if (settingsSubmenu === 'audio') {
      const selectedOption = selectedAudioLanguage || { id: '', label: '', languageKey: '' };
      const audioOptions =
        dubbedLanguages?.map((track) => ({
          id: track.languageKey,
          label: track.label,
          languageKey: track.languageKey,
          role: 'menuitemradio',
        })) || [];
      settingsSubmenuConfig = {
        id: 'audio',
        title: _t('Audio'),
        selected: selectedOption,
        options: audioOptions,
        action: (option) => {
          const previousSelectedSubtitleLanguage = currentTrack?.language ?? VIDEO_PLAYER_SUBTITLE_NOT_SET;
          const isOriginalAudio = option.languageKey === defaultLanguageKey;
          const audioLanguageVideoMetadata = {
            audioLanguage: getEventingDubbingLanguage(defaultLanguageKey, option.languageKey),
            previousAudioLanguage: getEventingDubbingLanguage(defaultLanguageKey, selectedOption.languageKey),
          };

          onDubbedAudioChoiceClick?.(option as { id: string; languageKey: string; label: string });

          // There is a race condition between the video player metadata function in the VideoPlayer.tsx
          // file and the useDubbing hook's functionality that updates the subtitle languge that
          // caused the subtitles language video metadata to not be up to date when calling
          // the trackVideoPlayerClickEvent function so it had to explicitly be handled here if the subtitles
          // language is set. If subitiles are not enabled, the dubbing hook doesn't update the subtitle language
          if (userPreferences.get('subtitleLanguage') === VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET) {
            trackVideoPlayerClickEvent('video_settings_menu_audio_language_option', {
              ...audioLanguageVideoMetadata,
            });
          } else {
            trackVideoPlayerClickEvent('video_settings_menu_audio_language_option', {
              ...audioLanguageVideoMetadata,
              previousSubtitleLanguage: previousSelectedSubtitleLanguage,
              subtitleLanguage: isOriginalAudio
                ? userPreferences.get('subtitleLanguage')
                : option.languageKey ?? VIDEO_PLAYER_SUBTITLE_NOT_SET,
            });
          }

          closeSettingsMenu();
        },
      };
    }

    const handleSubmenuBackButtonClick = (ev: MouseEvent<HTMLButtonElement>) => {
      ev.stopPropagation();
      setSettingsSubmenu(undefined);
    };

    const mainSettingsMenuConfig: ModernSettingsMenuItemType[] = [
      {
        title: _t('Autoplay'),
        icon: <PlayCircleSvg />,
        buttonWrapper: false,
        actionComponent: (
          <Switch
            // Since I provide an appropriate aria-label here that matches the title, I don't need to provide an aria-labelledby attribute. If I set it as
            // undefined, it will use the default label, so I set it to an empty string.
            aria-labelledby=""
            aria-label={_t('Autoplay')}
            selected={autoplay}
            hideTooltip={true}
            onToggle={() => {
              onToggleAutoplay();
              trackVideoPlayerClickEvent('video_settings_menu_autoplay_toggle', { autoplay });
            }}
          />
        ),
      },
      {
        title: _t('Quality'),
        icon: <HighQualitySvg />,
        action: () => {
          setSettingsSubmenu('quality');
          trackVideoPlayerClickEvent('video_settings_menu_quality', {});
        },
        actionComponent: <ChevronNextIcon size="small" css={styles.boldIcon} />,
      },
      {
        title: _t('Playback speed'),
        icon: <PlaybackSpeedSvg />,
        action: () => {
          setSettingsSubmenu('playbackSpeed');
          trackVideoPlayerClickEvent('video_settings_menu_playback_speed', {});
        },
        actionComponent: <ChevronNextIcon size="small" css={styles.boldIcon} />,
      },
      {
        title: _t('Subtitles'),
        icon: <SubtitlesSvg />,
        action: () => {
          setSettingsSubmenu('subtitle');
          trackVideoPlayerClickEvent('video_settings_menu_subtitle_language', {});
        },
        actionComponent: <ChevronNextIcon size="small" css={styles.boldIcon} />,
      },
    ];

    if (isDubbingAvailable) {
      mainSettingsMenuConfig.push({
        title: _t('Audio'),
        icon: <HeadsetSvg />,
        action: () => {
          setSettingsSubmenu('audio');
          trackVideoPlayerClickEvent('video_settings_menu_audio_language', {});
        },
        actionComponent: <ChevronNextIcon size="small" css={styles.boldIcon} />,
      });
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
      <div
        ref={ref}
        css={[styles.settingsMenuPopup, visible && styles.popupVisible, isDubbingAvailable && styles.scrollbar]}
        className="rc-ModernSettingsMenuPopup"
        // We want to ensure that all clicks in this popup (and child components/elements) don't bubble up to the video player and end here
        onClick={(e) => {
          e.stopPropagation();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
        }}
        data-testid="settingsMenuPopup"
      >
        {settingsSubmenuConfig ? (
          <ModernSettingsSubmenu
            {...settingsSubmenuConfig}
            visible={visible}
            onBackButtonClick={handleSubmenuBackButtonClick}
          />
        ) : (
          <ModernSettingsMenuItemList visible={!settingsSubmenuConfig && visible} menuConfig={mainSettingsMenuConfig} />
        )}
      </div>
    );
  }
);

export default ModernSettingsMenuPopup;
